import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  CalendarIcon,
  LocationMarkerIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
  ChevronLeftIcon,
  PrinterIcon,
  PaperClipIcon,
  MailIcon,
  ClipboardCopyIcon,
  ExclamationIcon,
  LockClosedIcon,
} from '@heroicons/react/outline';

import { Link, useNavigate, useParams } from 'react-router-dom';
import PortableText from 'react-portable-text';
import { jobViewQuery } from '../utils/Data';
import { client } from '../client';
import { NotFound } from '../pages';
import { formatTitle } from '../utils/formatTitle';
import site from '../site.json';
import ApplyModal, { appliedOn } from './ApplyModal';
import { nativeApply } from '../utils/applyConfig';
import { feature } from '../utils/features';
import JobDetailModern from './JobDetailModern';
import { LoadingBlock } from './Spinner';
import { NoResults } from './ResultsState';

// Renders links from job content. Only http(s) and mailto URLs become links, so a
// posting can't smuggle in a javascript: URL. Anything else renders as plain text.
const SAFE_HREF = /^(https?:|mailto:)/i;

const SafeLink = ({ href, children }) => {
  const url = typeof href === 'string' ? href.trim() : '';
  if (!SAFE_HREF.test(url)) {
    return <span>{children}</span>;
  }
  const isWeb = /^https?:/i.test(url);
  return (
    <a
      href={url}
      className=" text-primary hover:underline cursor-pointer"
      {...(isWeb && { target: '_blank', rel: 'noopener noreferrer nofollow' })}
    >
      {children}
    </a>
  );
};

// Wraps a mailto link. If clicking it doesn't hand off to a mail app within
// FALLBACK_DELAY_MS (no window blur, our best signal an app opened), we assume
// the browser has no mail handler registered and show a copy-the-address fallback.
const FALLBACK_DELAY_MS = 1200;

const MailtoLink = ({ email, subject, body, className, disabledClassName, children }) => {
  const [showFallback, setShowFallback] = useState(false);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);
  const blurredRef = useRef(false);

  const href = email
    ? `mailto:${email}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`
    : undefined;

  const handleClick = () => {
    if (!email) return;
    setShowFallback(false);
    setCopied(false);
    blurredRef.current = false;

    const onBlur = () => {
      blurredRef.current = true;
    };
    window.addEventListener('blur', onBlur);

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      window.removeEventListener('blur', onBlur);
      if (!blurredRef.current) {
        setShowFallback(true);
      }
    }, FALLBACK_DELAY_MS);
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(email)
      .then(() => setCopied(true))
      .catch((err) => console.log(err));
  };

  return (
    <>
      <a
        href={href}
        onClick={handleClick}
        className={`${className} ${!email ? disabledClassName : ''}`}
      >
        {children}
      </a>

      <Transition.Root show={showFallback} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 overflow-y-auto"
          onClose={() => setShowFallback(false)}
        >
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="relative inline-block w-full max-w-sm p-6 my-8 text-left align-middle bg-white rounded-lg shadow-xl">
                <Dialog.Title className="text-lg font-bold font-[Poppins] text-gray-900">
                  Let's get your application sent
                </Dialog.Title>
                <p className="mt-2 text-sm text-gray-600">
                  We couldn't find an email app set up on this browser, so we
                  couldn't open one automatically. No problem, just copy the
                  address below and send your application (resume and cover
                  letter) directly to apply.
                </p>

                <div className="mt-4 flex items-center justify-between gap-2 border border-gray-200 rounded-md bg-gray-50 px-3 py-2">
                  <span className="font-medium text-gray-900 text-sm truncate">
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 text-primary text-xs font-semibold whitespace-nowrap"
                  >
                    <ClipboardCopyIcon className="h-4 w-4" aria-hidden="true" />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setShowFallback(false)}
                  className="mt-5 w-full inline-flex justify-center px-4 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Got it
                </button>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

const EmployerInfoCard = ({ name, fullAddress, email, bio, jobTitle, showEmail = true }) => {
  return (
    <div className=" lg:w-1/4 sm:px-4 sm:mx-4 my-8 border border-gray-200 p-4 rounded-lg bg-gray-100">
      <p className="font-medium  mb-6 font-[Poppins] text-primary text-lg mr-12">
        Posted By:
      </p>
      <div className="flex flex-row items-center justify-between mb-2">
        <h2 className="font-bold font-[Poppins] text-gray-800 text-lg mr-12">
          {name}
        </h2>

        {showEmail && (
          <MailtoLink
            email={email}
            subject={`Application for ${jobTitle}`}
            body="Please find attached my resume and cover letter to apply."
            className="h-6 w-6 text-gray-800 ml-12 inline-block"
            disabledClassName="opacity-50 pointer-events-none"
          >
            <MailIcon />
          </MailtoLink>
        )}
      </div>
      <div className="flex mb-6 items-center justify-start">
        <LocationMarkerIcon className="h-6 w-6 text-gray-800 mr-2" />
        <p>{fullAddress}</p>
      </div>

      <PortableText
        className=""
        content={bio}
        projectId={process.env.REACT_APP_SANITY_PROJECT_ID}
        dataset={process.env.REACT_APP_SANITY_DATASET}
        serializers={{
          h1: (props) => (
            <h1
              className="text-2xl text-gray-800 font-bold mb-2"
              {...props}
            ></h1>
          ),
          h2: (props) => (
            <h2
              className="text-xl text-gray-800 font-bold mb-2"
              {...props}
            ></h2>
          ),
          h3: (props) => (
            <h3
              className="text-xl text-gray-800 font-bold mb-2"
              {...props}
            ></h3>
          ),
          h4: (props) => (
            <h4
              className="text-lg text-gray-800 font-semibold mb-2"
              {...props}
            ></h4>
          ),
          li: (props) => <li className="list-disc my-2 ml-4" {...props}></li>,
          link: SafeLink,
          normal: (props) => <p className="" {...props}></p>,
        }}
      />
    </div>
  );
};

function JobView() {
  const history = useNavigate();

  const [jobData, setJobData] = useState({
    _id: '',
    body: [],
    categories: '',
    vacancies: '',
    city: '',
    email: '',
    companyBio: [],
    companyName: '',
    location: '',
    salary: ' ',
    title: '',
    postedOn: '',
    postExpiry: '',
  });

  // Take params from the link and use it to fetch job details
  const { btnId } = useParams();
  // Native apply (FEAT-APPLY): on for this board, or in test mode with ?applytest=1. Otherwise email Apply as before.
  const applyCfg = nativeApply();
  const [applyOpen, setApplyOpen] = useState(false);
  const [appliedAt, setAppliedAt] = useState(() => appliedOn(btnId));

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchJobDetails = () => {
    setLoading(true);
    setLoadError(false);
    client
      .fetch(jobViewQuery(btnId))
      .then((data) => {
        setJobData(data[0]);
      })
      .catch((err) => {
        console.log(err);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchJobDetails();
  }, [btnId]);

  if (loading) {
    return <LoadingBlock label="Loading job…" minHeight="min-h-[60vh]" />;
  }
  if (loadError) {
    return <NoResults error />;
  }
  if (!jobData) {
    return <NotFound />;
  }

  const expiryDate = new Date(jobData.postExpiry);
  const expiryDateCourtesy = expiryDate.setDate(expiryDate.getDate() + 1);
  const currentDate = new Date();
  const isPostExpired = expiryDateCourtesy < currentDate;
  const displayTitle = formatTitle(jobData.title);

  // postExpiry is a plain YYYY-MM-DD date; format it in UTC so it doesn't shift a day.
  const closedOn = jobData.postExpiry
    ? new Date(`${jobData.postExpiry}T00:00:00Z`).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : '';

  const applyModal = applyCfg && !isPostExpired && (
    <ApplyModal
      open={applyOpen}
      onClose={() => setApplyOpen(false)}
      job={{ _id: btnId, title: displayTitle, companyName: jobData.companyName }}
      config={applyCfg}
      onApplied={setAppliedAt}
    />
  );

  if (feature('modernJobPage')) {
    return (
      <>
        <JobDetailModern
          {...{ btnId, jobData, displayTitle, isPostExpired, closedOn, applyCfg, appliedAt, SafeLink, MailtoLink }}
          onApply={() => setApplyOpen(true)}
          onBack={() => history(-1)}
        />
        {applyModal}
      </>
    );
  }

  return (
    <>
      <div className="px-4 mx-4">
        <button onClick={() => history(-1)}>
          <div className="text-lg font-semibold text-gray-900 flex flex-row items-center">
            <ChevronLeftIcon className="text-gray-900 h-5 w-5" />
            <h1>Back</h1>
          </div>
        </button>

        <div>
          {/* JOB CONTENT */}
          <div className="flex flex-wrap -m-4 mt-8 ">
            <div className="w-full mb-4 ">
              {isPostExpired && (
                <div
                  role="status"
                  className="flex items-start border border-yellow-300 bg-yellow-50 p-4 rounded-lg mb-2 md:mx-4"
                >
                  <ExclamationIcon
                    className="flex-shrink-0 h-6 w-6 text-yellow-600 mr-3"
                    aria-hidden="true"
                  />
                  <div className="text-sm text-gray-800">
                    <p className="font-semibold">This job has expired</p>
                    <p className="mt-1">
                      The employer stopped accepting applications
                      {closedOn ? ` on ${closedOn}` : ''}.{' '}
                      <Link
                        to="/jobs"
                        className="font-semibold text-primary hover:underline"
                      >
                        Browse other jobs
                      </Link>
                    </p>
                  </div>
                </div>
              )}
              <div className=" border border-gray-200 p-4 rounded-lg bg-gray-100 mb-2  md:mx-4">
                <div className="lg:flex lg:items-center lg:justify-between">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold leading-7 text-gray-900 ">
                      {displayTitle}
                    </h2>
                    <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <BriefcaseIcon
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                          aria-hidden="true"
                        />
                        {jobData.categories}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <LocationMarkerIcon
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                          aria-hidden="true"
                        />
                        {jobData.city}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <CurrencyDollarIcon
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                          aria-hidden="true"
                        />
                        {jobData.salary}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <CalendarIcon
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                          aria-hidden="true"
                        />
                        Posted on: {jobData.postedOn}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex lg:mt-0 lg:ml-4">
                    <span className="sm:block ">
                      {isPostExpired ? (
                        <button
                          type="button"
                          disabled
                          aria-disabled="true"
                          title="This job is no longer accepting applications"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 bg-gray-200 rounded-md text-sm font-medium text-gray-500 cursor-not-allowed"
                        >
                          <LockClosedIcon
                            className="-ml-1 mr-2 h-5 w-5 text-gray-500"
                            aria-hidden="true"
                          />
                          Applications closed
                        </button>
                      ) : applyCfg ? (
                        <button
                          type="button"
                          onClick={() => setApplyOpen(true)}
                          className="inline-flex items-center px-4 py-2 border border-primary bg-primary ease-in duration-100 hover:opacity-90 rounded-md shadow-sm text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          <PaperClipIcon className="-ml-1 mr-2 h-5 w-5 text-white" aria-hidden="true" />
                          {appliedAt ? 'Applied' : 'Apply'}
                        </button>
                      ) : (
                        <MailtoLink
                          email={jobData.email}
                          subject={`Application for ${displayTitle}`}
                          body="Please find attached my resume and cover letter to apply."
                          disabledClassName="opacity-50 pointer-events-none"
                        >
                          <button
                            type="button"
                            className="inline-flex items-center px-4 py-2 border border-primary bg-primary ease-in duration-100 hover:opacity-90 rounded-md shadow-sm text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            <PaperClipIcon
                              className="-ml-1 mr-2 h-5 w-5 text-white"
                              aria-hidden="true"
                            />
                            Apply
                          </button>
                        </MailtoLink>
                      )}
                    </span>

                    <span className=" sm:block ml-3">
                      <button
                        onClick={window.print}
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-primary ease-in duration-100 hover:bg-primary-light rounded-md shadow-sm text-sm font-medium text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        <PrinterIcon
                          className="-ml-1 mr-2 h-5 w-5 text-primary"
                          aria-hidden="true"
                        />
                        Print
                      </button>
                    </span>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="flex flex-col lg:flex-row justify-between items-start mb-24">
                <div className=" sm:2/4 lg:w-3/5 md:mx-4 sm:px-2 sm:mx-2 mt-8 ">
                  <article>
                    <PortableText
                      className=""
                      content={jobData.body}
                      projectId={process.env.REACT_APP_SANITY_PROJECT_ID}
                      dataset={process.env.REACT_APP_SANITY_DATASET}
                      serializers={{
                        h1: (props) => (
                          <h1
                            className="text-2xl text-gray-800 font-bold mb-2"
                            {...props}
                          ></h1>
                        ),
                        h2: (props) => (
                          <h2
                            className="text-xl text-gray-800 font-bold mb-2"
                            {...props}
                          ></h2>
                        ),
                        h3: (props) => (
                          <h3
                            className="text-xl text-gray-800 font-bold mb-2"
                            {...props}
                          ></h3>
                        ),
                        h4: (props) => (
                          <h4
                            className="text-lg text-gray-800 font-semibold mb-2"
                            {...props}
                          ></h4>
                        ),
                        li: (props) => (
                          <li className="list-disc my-2 ml-4" {...props}></li>
                        ),
                        link: SafeLink,
                        normal: (props) => <p className="" {...props}></p>,
                      }}
                    />
                  </article>

                  <div className=" mt-8 border border-gray-200 p-4 rounded-lg bg-gray-100">
                    <p className="font-medium  font-[Poppins] text-primary text-lg mr-12">
                      Additional Job Information:
                    </p>
                    {/* Job Status  */}
                    <p className="mt-2 text-sm sm:text-base">
                      <span className="font-semibold underline underline-offset-2 font-[Poppins] text-gray-700 mr-2">
                        Job Status:
                      </span>{' '}
                      <span
                        className={`font-medium text-gray-100 py-1 px-2 rounded-full ${
                          isPostExpired ? 'bg-red-700' : 'bg-green-600'
                        }`}
                      >
                        {isPostExpired ? 'Expired' : 'Active'}
                      </span>
                    </p>

                    <p className="mt-2 text-sm sm:text-base">
                      <span className="font-semibold underline underline-offset-2 font-[Poppins] text-gray-700 mr-2">
                        Job ID:
                      </span>{' '}
                      <span className="font-medium text-gray-700">
                        {site.jobIdPrefix}-{jobData._id.slice(0, 4) + jobData._id.slice(32)}
                      </span>
                    </p>
                    <p className="mt-2 text-sm sm:text-base">
                      <span className="font-semibold underline underline-offset-2 font-[Poppins] text-gray-700 mr-2">
                        Eligibility:
                      </span>{' '}
                      <span className="font-medium text-gray-700">
                        {site.eligibility}
                      </span>
                    </p>
                    <p className="mt-2 text-sm sm:text-base">
                      <span className="font-semibold underline underline-offset-2 font-[Poppins] text-gray-700 mr-2">
                        Vacancies:
                      </span>{' '}
                      <span className="font-medium text-gray-700">
                        {jobData.vacancies}
                      </span>
                    </p>
                    <p className="mt-2 text-sm sm:text-base">
                      <span className="font-semibold  underline underline-offset-2 font-[Poppins] text-gray-700 mr-2">
                        Location:
                      </span>{' '}
                      <span className="font-medium  text-gray-700">
                        {jobData.location ? jobData.location : jobData.city}
                      </span>
                    </p>
                    <p className="mt-2 text-sm sm:text-base">
                      <span className="font-semibold underline underline-offset-4 font-[Poppins] text-gray-700 mr-2">
                        Expiry Date:
                      </span>{' '}
                      <span className="font-medium text-gray-700">
                        {jobData.postExpiry}
                      </span>
                    </p>
                  </div>
                </div>

                <EmployerInfoCard
                  name={jobData.companyName}
                  fullAddress={jobData.city}
                  email={isPostExpired ? '' : jobData.email}
                  bio={jobData.companyBio}
                  jobTitle={displayTitle}
                  showEmail={!applyCfg}
                />
              </div>
            </div>
          </div>
        </div>

        {/* End of Job Content */}
      </div>
      {applyModal}
    </>
  );
}

export default JobView;
